import React, { useState } from 'react';
import { Input, Button, Tag } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

const T = {
  brand: '#FF4500',
  bg: '#FFFFFF',
  bgMuted: '#F8F9FA',
  border: '#E5E7EB',
  text: '#0F0F10',
  textSecondary: '#5C5C66',
};

interface KeywordsInputProps {
  /** When omitted or undefined (e.g. Ant Design Form injection), treated as []. */
  value?: string[] | null;
  onChange: (keywords: string[]) => void;
  disabled?: boolean;
  /** Visual variant — 'default' = neutral chip, otherwise an Ant color name */
  tagColor?: string;
}

const KeywordsInput: React.FC<KeywordsInputProps> = ({
  value,
  onChange,
  disabled,
  tagColor = 'default',
}) => {
  const keywords = value ?? [];
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !keywords.includes(trimmed)) {
      onChange([...keywords, trimmed]);
      setInputValue('');
    }
  };

  const handleRemove = (kw: string) => {
    onChange(keywords.filter((k) => k !== kw));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAdd();
    }
  };

  const isNeutral = tagColor === 'default';

  return (
    <div>
      {keywords.length > 0 && (
        <div style={{ marginBottom: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {keywords.map((kw, i) => (
            <Tag
              key={i}
              closable={!disabled}
              onClose={() => handleRemove(kw)}
              {...(isNeutral ? {} : { color: tagColor })}
              style={
                isNeutral
                  ? {
                      padding: '2px 8px',
                      fontSize: 12,
                      borderRadius: 4,
                      margin: 0,
                      background: T.bgMuted,
                      color: T.text,
                      border: `1px solid ${T.border}`,
                    }
                  : {
                      padding: '2px 8px',
                      fontSize: 12,
                      borderRadius: 4,
                      margin: 0,
                    }
              }
            >
              {kw}
            </Tag>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 6 }}>
        <Input
          placeholder="Type a keyword and press Enter"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          style={{ flex: 1, borderRadius: 6 }}
        />
        <Button
          icon={<PlusOutlined />}
          onClick={handleAdd}
          disabled={disabled || !inputValue.trim()}
          style={{
            borderRadius: 6,
            borderColor: T.border,
            color: T.textSecondary,
          }}
        >
          Add
        </Button>
      </div>
    </div>
  );
};

export default KeywordsInput;
